export const PROGRESS_THRESHOLDS = {
  ON_TRACK: 75,
  ON_PACE: 50,
  AT_RISK: 25,
} as const;

export const PACE_GAP = {
  AT_RISK: 10,
  OFF_TRACK: 25,
  AHEAD: 10,
} as const;

export type ProgressStatus = "on_track" | "on_pace" | "at_risk" | "off_track";

export interface PaceContext {
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  now?: Date;
}

export interface PaceResult {
  status: ProgressStatus;
  expectedPercent: number;
  gap: number;
  overdue: boolean;
}

const QUARTER_START_MONTH: Record<string, number> = {
  Q1: 0,
  Q2: 3,
  Q3: 6,
  Q4: 9,
};

export function getQuarterRange(
  year: number,
  quarter: string,
): { start: Date; end: Date } | null {
  const startMonth = QUARTER_START_MONTH[quarter];
  if (startMonth === undefined) return null;
  const start = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0));
  const end = new Date(Date.UTC(year, startMonth + 3, 0, 23, 59, 59));
  return { start, end };
}

export function buildKrPaceContext(
  kr: {
    quarter: string;
    due_date?: string | null;
    created_at?: string | null;
  },
  year: number | undefined,
  now?: Date,
): PaceContext {
  const quarterRange =
    year !== undefined ? getQuarterRange(year, kr.quarter) : null;

  const dueDate = parseDate(kr.due_date);
  const createdDate = parseDate(kr.created_at);

  const endDate = dueDate ?? quarterRange?.end ?? null;

  let startDate: Date | null = null;
  if (quarterRange && createdDate) {
    startDate =
      quarterRange.start.getTime() > createdDate.getTime()
        ? quarterRange.start
        : createdDate;
  } else if (quarterRange) {
    startDate = quarterRange.start;
  } else if (createdDate) {
    startDate = createdDate;
  }

  return { startDate, endDate, now };
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function computePaceResult(
  completionPercent: number,
  pace: PaceContext,
): PaceResult | null {
  const startMs = pace.startDate?.getTime();
  const endMs = pace.endDate?.getTime();
  if (
    startMs === undefined ||
    endMs === undefined ||
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  ) {
    return null;
  }

  const nowMs = (pace.now ?? new Date()).getTime();
  const completion = Math.max(0, Math.min(100, completionPercent));

  let expected: number;
  let overdue = false;
  if (nowMs <= startMs) {
    expected = 0;
  } else if (nowMs >= endMs) {
    expected = 100;
    overdue = completion < 100;
  } else {
    expected = ((nowMs - startMs) / (endMs - startMs)) * 100;
  }
  expected = Math.max(0, Math.min(100, expected));

  const gap = expected - completion;

  let status: ProgressStatus;
  if (completion >= 100) {
    status = "on_track";
  } else if (overdue) {
    status = "off_track";
  } else if (gap > PACE_GAP.OFF_TRACK) {
    status = "off_track";
  } else if (gap > PACE_GAP.AT_RISK) {
    status = "at_risk";
  } else if (gap < -PACE_GAP.AHEAD) {
    status = "on_track";
  } else {
    status = "on_pace";
  }

  return { status, expectedPercent: expected, gap, overdue };
}

export function getProgressStatus(
  value: number,
  pace?: PaceContext,
): ProgressStatus {
  if (pace) {
    const result = computePaceResult(value, pace);
    if (result) return result.status;
  }
  const clamped = Math.max(0, Math.min(100, value));
  if (clamped >= PROGRESS_THRESHOLDS.ON_TRACK) return "on_track";
  if (clamped >= PROGRESS_THRESHOLDS.ON_PACE) return "on_pace";
  if (clamped >= PROGRESS_THRESHOLDS.AT_RISK) return "at_risk";
  return "off_track";
}

const STATUS_HSL_VAR: Record<ProgressStatus, string> = {
  on_track: "var(--info)",
  on_pace: "var(--primary)",
  at_risk: "var(--accent)",
  off_track: "var(--destructive)",
};

export function getProgressColor(value: number, pace?: PaceContext): string {
  return `hsl(${STATUS_HSL_VAR[getProgressStatus(value, pace)]})`;
}

const STATUS_LABEL: Record<ProgressStatus, string> = {
  on_track: "On track",
  on_pace: "On pace",
  at_risk: "At risk",
  off_track: "Off track",
};

export function getProgressLabel(value: number, pace?: PaceContext): string {
  return STATUS_LABEL[getProgressStatus(value, pace)];
}
